/**
 * Global ErrorBoundary — prevents the dreaded "white page of death"
 * when a single component throws during render. Falls back to a friendly
 * card with the error message and a "Reload" button.
 *
 * Wrap around the entire <App/> routes tree so any uncaught render error
 * is captured instead of unmounting the whole React tree.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught:', error, info);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || String(this.state.error || 'Unknown error');
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50 p-6"
        >
          <div className="max-w-lg w-full rounded-2xl border-2 border-rose-200 bg-white shadow-xl p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-rose-600" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">حدث خطأ في الصفحة</h2>
            <p className="text-sm text-muted-foreground mb-3">
              صادفنا مشكلة في عرض هذه الصفحة. تم تسجيل الخطأ في console — يمكنك إعادة المحاولة.
            </p>
            <pre
              data-testid="error-boundary-message"
              className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 mb-4 text-left overflow-auto max-h-32 font-mono"
              dir="ltr"
            >
              {msg}
            </pre>
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={this.handleReload}
                data-testid="error-boundary-reload-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition"
              >
                <RefreshCw className="w-4 h-4" /> إعادة تحميل
              </button>
              <button
                type="button"
                onClick={this.handleHome}
                data-testid="error-boundary-home-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-border bg-white text-sm font-semibold hover:bg-secondary/50 transition"
              >
                <Home className="w-4 h-4" /> الرئيسية
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
